import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import moment from 'moment'
import { Link, useHistory, useLocation } from 'react-router-dom'
import classnames from 'classnames'
import smoothscroll from 'smoothscroll-polyfill'

import { PredictionDifferenceBox } from '../../Shared/PredictionBox'
import { formatEventSubheadlineLocation } from '../../../utils/helpers'

import { VenueIcon, ClockIcon, PinIcon, TickWrappedIcon } from '../../../images'

smoothscroll.polyfill()

const EventRow = ({
    event,
    shouldScrollOnLoad,
    children,
    onClick,
    isSelected = false,
    isDisabled = false,
    classNames = '',
    contentClassNames = '',
    targetUrlPrefix = '',
    testid = 'DEFAULT-TEST-ID'
}) => {
    const rowRef = useRef(null)
    const history = useHistory()
    const location = useLocation()

    const { t } = useTranslation()

    const { id, title, date, now, benchmark } = event
    const diff = Number(now - benchmark)

    // listens for breakpoints desktop => mobile && vice versa
    const [viewWidth, setViewWidth] = useState(window.innerWidth)
    const handleResize = () => setViewWidth(window.innerWidth)

    useEffect(() => {
        window.addEventListener('resize', handleResize)
        return () => {
            window.removeEventListener('resize', handleResize)
        }
    }, [])

    const handleRowClick = () => {
        if (onClick) onClick(event.id)
        if (isDisabled || viewWidth <= 975) return

        history.push(`?drawer-open=true&event-id=${event.id}`)
    }

    // building the url through logic of /(campaigns|events)/(mobile|desktop)/eventId
    const pageUrl =
        `${viewWidth < 976 ? `${location.pathname}${targetUrlPrefix}/` : '?drawer-open=true&event-id='}${id}`

    useLayoutEffect(() => {
        if (isSelected && shouldScrollOnLoad && rowRef.current && rowRef.current.offsetTop) {
            window.scrollTo({ top: rowRef.current.offsetTop, behavior: 'smooth' })
        }
    }, [isSelected, shouldScrollOnLoad])

    const showActiveBadge = moment(event.date) > moment()

    return (
        <div
            ref={rowRef}
            className={classnames({
                'event-row_outer-wrapper': true,
                highlighted: isSelected,
                disabled: isDisabled,
                [classNames]: !!classNames,
            })}
            onClick={handleRowClick}
            data-testid={testid}
        >

            <Link
                className="event-row_inner-wrapper"
                to={isDisabled ? '#' : pageUrl}
            >
                <div className="event-row_left-content_title_small">{title}</div>
                <div className={classnames({
                    'event-row_left-content_wrapper': true,
                    [contentClassNames]: !!contentClassNames,
                })}>
                    <div className="event-row_left-content_img">
                        <i className='icon-events' />
                    </div>
                    <div className="event-row_left-content_title-and-date_wrapper">
                        <div className="event-row_left-content_title-and-date_title">
                            {title}
                        </div>
                        <div className="event-row_left-content_title-and-date_date_wrapper d-flex">
                            <span className="event-row_left-content_title-and-date_date_time">
                                <span>
                                    <ClockIcon className='mr-1' height={12} width={17} />
                                </span>
                                <time dateTime={moment.utc(date).format('DD.MM.YYYY, HH:mm')}>
                                    {moment.utc(date).format('DD.MM.YYYY, HH:mm')}
                                </time>
                            </span>
                            <span className='event-row_left-content_title-and-date_date_location'>
                                <span>
                                    <PinIcon className='mr-1' height={12} width={17} />
                                </span>
                                <span>{
                                    formatEventSubheadlineLocation({
                                        city: event.city,
                                    })
                                }</span>
                            </span>
                            <span className='event-row_left-content_title-and-date_date_location'>
                                <span>
                                    <VenueIcon className='mr-1' height={12} width={17} />
                                </span>
                                <span>{
                                    formatEventSubheadlineLocation({
                                        hall: event.hall_name,
                                        venue: event.venue_name
                                    })
                                }</span>
                            </span>
                            {event.has_active_campaigns && (
                                <span
                                    className={`event-row_left-content_title-and-date_date_active-campaign-indicator_wrapper ${showActiveBadge ? 'green': 'red'}`}
                                >
                                    <span><TickWrappedIcon width={13} /></span>
                                    <span className="event-row_left-content_title-and-date_date_active-campaign-indicator_text">
                                        {showActiveBadge ? t('Recommendations.campaignBadge.active') : 
                                            t('Recommendations.campaignBadge.inactive')}
                                    </span>
                                </span>
                            )}
                        </div>
                    </div>
                </div>
                <div className="event-row_middle-content_wrapper">
                    {diff !== 0 && <PredictionDifferenceBox difference={{ number: diff }} />}
                </div>
                {children}
            </Link >
        </div >
    )

}

EventRow.propTypes = {
    event: PropTypes.object.isRequired,
    shouldScrollOnLoad: PropTypes.bool,
    children: PropTypes.node,
    onClick: PropTypes.func,
    targetUrlPrefix: PropTypes.string,
    classNames: PropTypes.string,
    contentClassNames: PropTypes.string,
    isSelected: PropTypes.bool,
    isDisabled: PropTypes.bool,
    testid: PropTypes.string,
}

export default EventRow
